import type { UserInfo } from '/#/store';
import { defineStore } from 'pinia';
import { store } from '/@/store';
import { RoleEnum } from '/@/enums/roleEnum';
import { PageEnum } from '/@/enums/pageEnum';
import {
  IN_BATCH_WATERMARK_WHITELIST,
  ROLES_KEY,
  TOKEN_KEY,
  USER_INFO_KEY,
} from '/@/enums/cacheEnum';
import { getAuthCache, setAuthCache } from '/@/utils/auth';
import { useMessage } from '/@/hooks/web/useMessage';
import { router } from '/@/router';
import { usePermissionStore } from '/@/store/modules/permission';
import { RouteRecordRaw } from 'vue-router';
import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic';
import { GetUserInfoModel } from '@lingtu/apis/entities';
import { apis } from '/@/apis';
import { getAuthManager, getUserManager } from '@lingtu/user';
import { IListComplexOrgsRes } from '@lingtu/apis/entities';
import { dictStore } from './dict';
import { usePermissionsStore } from './permissions';

function getRootDomain() {
  const domain = window.location.hostname;
  const parts = domain.split('.');
  const rootDomain = parts.slice(-2).join('.');
  return '.' + rootDomain;
}

export function handleUtm(ucode) {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 10);
  document.cookie = `ucode=${ucode}; path=/;expires=${date.toUTCString()};domain=${getRootDomain()};`;
}

const authManager = getAuthManager();

interface UserState {
  userInfo: Nullable<UserInfo>;
  token?: string;
  roleList: RoleEnum[];
  sessionTimeout?: boolean;
  isLogoutByOverridden: boolean;
  lastUpdateTime: number;
  orgs: Nullable<IListComplexOrgsRes>;
  wxOpenId: Nullable<string>;
  members: Array<any>;
}

export const useUserStore = defineStore({
  id: 'app-user',
  state: (): UserState => ({
    // user info
    userInfo: null,
    // token
    token: undefined,
    // roleList
    roleList: [],
    // Whether the login expired
    sessionTimeout: false,
    isLogoutByOverridden: false,
    // Last fetch time
    lastUpdateTime: 0,
    // 组织列表
    orgs: null,
    // 微信浏览器访问有openid
    wxOpenId: null,
    // 当前组织的成员
    members: [],
  }),
  getters: {
    // 组织列表数组
    orgList() {
      return [...(this.orgs?.personal || []), ...(this.orgs?.orgs || [])];
    },

    // 组织列表是否包含个人组织
    hasPersonalOrg() {
      return !!this.orgs?.personal?.length;
    },

    // 组织列表是否包含多人组织
    hasTeamOrg() {
      return !!this.orgs?.orgs?.length;
    },
    isLogion(): Boolean {
      return !!this.userInfo?.userId;
    },
    getUserInfo(): UserInfo {
      return this.userInfo || getAuthCache<UserInfo>(USER_INFO_KEY) || {};
    },
    getToken(): string {
      return this.token || getAuthCache<string>(TOKEN_KEY);
    },
    getRoleList(): RoleEnum[] {
      return this.roleList.length > 0 ? this.roleList : getAuthCache<RoleEnum[]>(ROLES_KEY);
    },
    getSessionTimeout(): boolean {
      return !!this.sessionTimeout;
    },
    getLastUpdateTime(): number {
      return this.lastUpdateTime;
    },
    memberList(): Array<{ label: string; value: string }> {
      return this.members
        .map((item) => {
          let label = item.memberName;
          if (item.userId === this.userInfo?.userId) {
            label += '（我）';
          }
          if (item.status === 'removed') {
            label += '（已移除）';
          }
          return {
            label,
            value: item.userId,
            status: item.status,
          };
        })
        .sort((v) => {
          if (v.status === 'removed') {
            return 1;
          }
          return -1;
        })
        .sort((v) => {
          if (v.value === this.userInfo?.userId) {
            return -1;
          }
          return 0;
        });
    },
  },
  actions: {
    setToken(info: string | undefined) {
      this.token = info ? info : ''; // for null or undefined value
      setAuthCache(TOKEN_KEY, info);
    },
    setRoleList(roleList: RoleEnum[]) {
      this.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setUserInfo(info) {
      this.userInfo = {
        ...this.userInfo,
        ...info,
      };
      this.lastUpdateTime = new Date().getTime();
      setAuthCache(USER_INFO_KEY, info);
    },
    setSessionTimeout(flag: boolean) {
      this.sessionTimeout = flag;
    },
    resetState() {
      this.userInfo = null;
      this.token = '';
      this.roleList = [];
      this.sessionTimeout = false;
      localStorage.removeItem(IN_BATCH_WATERMARK_WHITELIST);
      usePermissionsStore(store).setPromptWordsEnabled(false);
    },

    codeToWxOpenId(code: string) {
      this.wxOpenId = code;
    },

    /**
     * @description: login
     */
    async login() {
      try {
        const accessToken = await authManager.silentLogin();
        // save token
        if (accessToken) {
          this.setToken(accessToken);
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },
    async afterLoginAction(goHome?: boolean): Promise<GetUserInfoModel | null> {
      const accessToken = await authManager.getAccessToken();
      if (!accessToken) return null;
      // get user info
      const userInfo = await this.getUserInfoAction();

      const sessionTimeout = this.sessionTimeout;
      if (sessionTimeout) {
        this.setSessionTimeout(false);
      } else {
        const permissionStore = usePermissionStore();
        if (!permissionStore.isDynamicAddedRoute) {
          const routes = await permissionStore.buildRoutesAction();
          routes.forEach((route) => {
            router.addRoute(route as unknown as RouteRecordRaw);
          });
          router.addRoute(PAGE_NOT_FOUND_ROUTE as unknown as RouteRecordRaw);
          permissionStore.setDynamicAddedRoute(true);
        }
        goHome && (await router.replace(userInfo?.homePath || PageEnum.BASE_HOME));
      }
      return userInfo;
    },
    async getUserInfoAction(): Promise<UserInfo | null> {
      const userManager = getUserManager();
      const userInfo = await userManager.getUser(true);
      if (!userInfo) return null;
      this.setUserInfo(userInfo);
      void this.syncBatchWatermarkWhitelist(userInfo.userId);
      void usePermissionsStore(store).syncPromptWordsPermission(userInfo.userId);
      return userInfo;
    },

    async syncBatchWatermarkWhitelist(userId?: string) {
      if (!userId) {
        localStorage.removeItem(IN_BATCH_WATERMARK_WHITELIST);
        return;
      }

      const whitelist = await dictStore(store).getBatchWatermarkWhitelist();
      if (whitelist?.includes(userId)) {
        localStorage.setItem(IN_BATCH_WATERMARK_WHITELIST, 'true');
      } else {
        localStorage.removeItem(IN_BATCH_WATERMARK_WHITELIST);
      }
    },

    async getOrgList() {
      if (!this.orgs) {
        const res = await apis.ems.listComplexOrgs();
        this.orgs = res;
      }
      return this.orgs;
    },

    async setOrgList() {
      const res = await apis.ems.listComplexOrgs();
      this.orgs = res;
    },

    // 切换用户的工作流
    async switchSolution(code: string) {
      await apis.users.switchSolution(code);
      const userInfo = await this.getUserInfoAction();
      return userInfo;
    },

    /**
     *
     *
     * orgId 绑定的id
     okText 二次确认弹窗弹窗ok文案
     content 二次确认弹窗弹窗文案
     isShowConfirm 是否展示二次确认弹窗
     isRedirect 切换后是否重定向
     redirectUri 切换后的重定向地址
     isUseRecent 是否使用最近使用的租户id
     */
    async selectOrg({
      orgId = '',
      okText = '确认并切换',
      content = '请先确保所有工具页已妥善关闭，未保存的作品可能会丢失哦~',
      isShowConfirm = true,
      isRedirect = true,
      isUseRecent = false,
      redirectUri = '/aiad',
    }) {
      // 使用最近的orgId
      const getRecentOrgId = () => {
        const orgs = this.orgs;
        const prevOrgId = localStorage.getItem('orgId');
        // 判断上一次绑定id是否在组合列表内
        const isUserPrevOrgId =
          prevOrgId &&
          (!!this.orgs?.orgs?.find((v) => v.orgId === prevOrgId) ||
            !!orgs?.personal?.find((v) => v.orgId === prevOrgId));
        const firstOrgId = orgs?.personal?.[0]?.orgId || orgs?.orgs?.[0]?.orgId;
        return isUserPrevOrgId ? prevOrgId : firstOrgId;
      };

      const select = async () => {
        const id = isUseRecent ? getRecentOrgId() : orgId;
        if (id) {
          await authManager.selectOrg(id);
          this.saveOrgId(id);
        }

        if (isRedirect) {
          location.href = redirectUri;
        }
      };

      if (!isShowConfirm) {
        await select();
        return;
      }

      const { createConfirm } = useMessage();
      createConfirm({
        iconType: 'warning',
        title: () => '切换个人/团队',
        content: () => content,
        zIndex: 3000,
        okText,
        cancelText: '取消',
        onOk: async () => {
          await select();
        },
      });
    },

    saveOrgId(orgId: string) {
      localStorage.setItem('orgId', orgId);
    },

    /**
     * @description: logout
     */
    async logout() {
      authManager.logout({
        redirectUri: location.pathname + location.search,
      });
    },

    confirmLoginOut() {
      const { createConfirm } = useMessage();
      createConfirm({
        iconType: 'warning',
        title: () => '退出登录',
        content: () => '确定要退出登录吗？请先确保所有工具页已妥善关闭，未保存的作品可能会丢失哦～',
        okText: '退出登录',
        cancelText: '取消',
        onOk: async () => {
          this.logout();
        },
      });
    },

    logoutByOverridden() {
      if (this.isLogoutByOverridden) return;
      this.isLogoutByOverridden = true;
      const { createConfirm } = useMessage();
      createConfirm({
        iconType: 'warning',
        title: () => '账号登录异常',
        content: () => '你的账号已在别处登录，请重新登录。如非本人操作，建议你尽快修改密码。',
        okText: '重新登录',
        cancelText: () => ' ',
        onOk: async () => {
          this.logout();
        },
      });
    },

    goLogin(redirectUri = '') {
      const url = new URL(window.location.href);
      const params = new URLSearchParams(url.search);

      const loginParams: any = {
        redirectUri: redirectUri || location.pathname + location.search,
      };
      if (params.has('case_id')) {
        handleUtm('selected-cases');
        loginParams.loginPageUri =
          (location.origin.includes('localhost')
            ? 'https://chore-dev.lingvisions.com'
            : location.origin) + `/cases/more/recommend?case_id=${params.get('case_id')}`;
      }

      authManager.login(loginParams);
    },

    createOrg() {
      // 存在个人组织
      if (this.orgs?.personal?.length) {
        router.push({
          name: 'CreateOrg',
          query: {
            type: 'team',
            only: true,
          },
        });
      } else {
        router.push({
          name: 'CreateOrg',
        });
      }
    },

    async getMembers() {
      if (this.members.length) {
        return;
      }
      const result = await apis.ems.listMembers({
        page: 1,
        pageSize: 999,
        includeRemoved: true,
      });
      this.members = result.items;
    },
  },
});

// Need to be used outside the setup
export function useUserStoreWithOut() {
  return useUserStore(store);
}
